<script setup>
import {computed} from "vue";
import FormInput from '@/components/Shared/Form/Input/Index.vue';

const emit = defineEmits(['paginate', 'update:per-page'])

const props = defineProps({
  pagination: {
    type: Object,
    required: true,
  },

  canSelectPerPage: {
    type: Boolean,
    default: false,
  },
})

const currentPage = computed(() => props.pagination.current_page)

const isCurrentPage = page => currentPage.value === page
const isPageInRange = page => Math.abs(page - currentPage.value) <= 1
const paginate = page => emit('paginate', {page: page})
</script>
<template>
  <div class="flex justify-around">
    <div v-if="canSelectPerPage">
      <div class="flex gap">
        <div>
          <form-input
            :model-value="pagination.per_page"
            :options="[10, 20, 50, 100]"
            type="select"
            @update:modelValue="emit('update:per-page', $event)"/>
        </div>
        <div class="flex flex-col justify-center">
          <p>
            {{
              trans('pagination.show_x_to_y_of_z_results', {
                from: pagination.from ?? 0,
                to: pagination.to ?? 0,
                total: pagination.total,
              })
            }}
          </p>
        </div>
      </div>
    </div>
    <nav v-show="pagination.total" class="flex justify-between items-center pager">
      <button
        class="pager_tile"
        rel="prev"
        v-text="trans('pagination.previous')"
        :disabled="!pagination.prev_page_url"
        @click="paginate(pagination.current_page - 1)"/>

      <button v-if="currentPage >= 3"
              class="pager_tile"
              v-text="`...`"
              @click="paginate(1)"/>

      <template v-for="page in pagination.last_page">
        <button
          v-if="isPageInRange(page)"
          :key="`pager-${page}`"
          :class="{ active: isCurrentPage(page) }"
          class="pager_tile"
          v-text="page"
          @click="paginate(page)"
        />
      </template>

      <button v-if="pagination.last_page >= 4 && currentPage + 1 < pagination.last_page"
              class="pager_tile"
              v-text="`...`"
              @click="paginate(pagination.last_page)"/>

      <button
        class="pager_tile"
        rel="next"
        v-text="trans('pagination.next')"
        :disabled="!pagination.next_page_url"
        @click="paginate(pagination.current_page + 1)"/>
    </nav>
  </div>
</template>
<style lang="scss" scoped>
.pager {
  width: 100%;
  max-width: 25rem;
  margin-left: auto;

  &_tile {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.75rem;
    background-color: var(--light);
    border: 1px solid var(--table-row-color);
    --length: 50px;
    height: var(--length);
    min-width: var(--length);

    &:not(.disabled) {
      cursor: pointer;
    }

    &.active {
      background: var(--theme);
      color: var(--light);
    }
  }
}
</style>
